/**
 * Painel de ranking de produtos (vendas ou margem).
 */

import React, { useState } from 'react'
import { Package, TrendingUp, Percent } from 'lucide-react'
import { resolveMediaUrl } from '../../../../utils/mediaUrl'
import { formatCurrencyBRL } from '../../../../utils/formatCurrency'
import './ProductRankingPanel.css'

function ProductRankingThumb({ imageUrl, name }) {
  const [failed, setFailed] = useState(false)
  const src = resolveMediaUrl(imageUrl)

  if (!src || failed) {
    return <Package size={18} aria-hidden />
  }

  return (
    <img
      src={src}
      alt={name ? `Foto de ${name}` : ''}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}

function formatCurrency(value, formatter) {
  if (formatter) return formatter(value)
  return formatCurrencyBRL(value)
}

function formatQuantity(value) {
  const n = parseFloat(value) || 0
  if (Number.isInteger(n)) return `${n} un`
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} un`
}

function ProductRankingPanel({
  title,
  subtitle,
  variant = 'sales',
  items = [],
  formatValue,
  onItemClick,
  emptyMessage = 'Nenhum produto no período'
}) {
  const Icon = variant === 'margin' ? Percent : TrendingUp

  return (
    <div className={`erp-product-ranking erp-product-ranking--${variant}`}>
      <div className="erp-product-ranking-header">
        <div>
          <h3 className="erp-product-ranking-title">
            <Icon size={16} className="erp-product-ranking-title-icon" />
            {title}
          </h3>
          {subtitle && <p className="erp-product-ranking-subtitle">{subtitle}</p>}
        </div>
        <span className="erp-product-ranking-badge">Top {items.length || 10}</span>
      </div>

      {items.length === 0 ? (
        <p className="erp-product-ranking-empty">{emptyMessage}</p>
      ) : (
        <ul className="erp-product-ranking-list">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="erp-product-ranking-row"
                onClick={() => onItemClick?.(item)}
                disabled={!onItemClick}
              >
                <span className={`erp-product-ranking-rank${item.rank <= 3 ? ` is-top-${item.rank}` : ''}`}>
                  {item.rank}
                </span>

                <div className="erp-product-ranking-thumb">
                  <ProductRankingThumb imageUrl={item.imageUrl} name={item.name} />
                </div>

                <div className="erp-product-ranking-info">
                  <p className="erp-product-ranking-name">{item.name}</p>
                  {item.sku && (
                    <p className="erp-product-ranking-sku">SKU {item.sku}</p>
                  )}
                </div>

                <div className="erp-product-ranking-metrics">
                  {variant === 'sales' ? (
                    <>
                      <p className="erp-product-ranking-metric-main">
                        {formatQuantity(item.quantitySold)}
                      </p>
                      <p className="erp-product-ranking-metric-sub">
                        {formatCurrency(item.revenue, formatValue)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="erp-product-ranking-metric-main">
                        {formatCurrency(item.totalMargin, formatValue)}
                      </p>
                      <p className="erp-product-ranking-metric-sub">
                        {(item.marginPercent || 0).toFixed(1)}% margem
                      </p>
                    </>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default ProductRankingPanel
